import { useMemo } from "react";
import { useFacilityCourses } from "modules/course/courseHooks";
import { useGetCurrentLocalFacilityId } from "@/modules/facility/facilityHooks";
import { Clubhouse_LeagueWizardScheduleMode } from "../../enums";
import { generateRoundPlan, type RoundPlan } from "./roundPlan";
import type { SeasonRoundItem } from "../leagueFormSchema";

export interface UseRoundPlanInput {
  seasonStart?: string;
  seasonEnd?: string;
  scheduleMode?: Clubhouse_LeagueWizardScheduleMode;
  weekDay?: number;
  flexRoundCount?: number;
  settings?: SeasonRoundItem["settings"];
  /** Layer deterministic per-round conditions over `settings`. */
  varyConditions?: boolean;
  // Round Settings must be answered before a plan is generated.
  ready: boolean;
}

/**
 * Single seam between Season Setup and the round plan. Today it derives the plan
 * client-side from the operator's answers; when the backend round-plan endpoint
 * ships, only this hook changes — its plan shape and every consumer stay the
 * same. `coursesReady` stays false until the facility course list has loaded.
 */
export function useRoundPlan({
  seasonStart,
  seasonEnd,
  scheduleMode,
  weekDay,
  flexRoundCount,
  settings,
  varyConditions,
  ready,
}: UseRoundPlanInput): { plan: RoundPlan | null; coursesReady: boolean } {
  const facilityId = useGetCurrentLocalFacilityId();
  const [courses, coursesQueryState] = useFacilityCourses(facilityId ?? "");
  // An empty facility id has no course query. A real facility stays unready
  // until that query finishes, so the plan is never stamped with course id 0.
  const coursesReady = !facilityId || !!coursesQueryState?.isFinished;

  // Keyed on the primitive inputs (not a freshly-built object) so the plan
  // identity is stable across renders when nothing relevant changed.
  const plan = useMemo(() => {
    if (
      !coursesReady ||
      !ready ||
      !seasonStart ||
      !seasonEnd ||
      scheduleMode == null
    ) {
      return null;
    }
    return generateRoundPlan({
      seasonStart,
      seasonEnd,
      scheduleMode,
      weekDay,
      flexRoundCount,
      settings,
      varyConditions,
      courses: courses.map((c) => ({ id: c.id, name: c.displayName })),
    });
  }, [
    coursesReady,
    ready,
    seasonStart,
    seasonEnd,
    scheduleMode,
    weekDay,
    flexRoundCount,
    settings,
    varyConditions,
    courses,
  ]);

  return { plan, coursesReady };
}
